import { LevelThreshold } from '@vae/server/src/services/achievementService/types/levelThreshold'
import { ChartOptions } from 'chart.js'
import { TFunction } from 'i18next'
import { getFontSize } from '../../../../utils/getFontSize'

export const getProgressionChartOptions = (
	daysInObservedMonth: number,
	maxValue: number,
	levelThreshold: LevelThreshold,
	isDarkMode: boolean,
	t: TFunction<'pages_saving', 'pointsProgressChart'>
): ChartOptions<'line'> => {
	const fontSize = getFontSize()

	const leftTickColor = isDarkMode ? '#3cb49f' : '#19b338'
	const gridColor = isDarkMode ? '#144448' : '#f5f5f5'
	const xTickColor = isDarkMode ? '#e3e3e3' : '#858585'
	const tooltipBackgroundColor = isDarkMode ? '#0d2d30' : '#ffffff'
	const tooltipTextColor = isDarkMode ? '#e3e3e3' : '#858585'

	return {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				mode: 'index',
				intersect: false,
				backgroundColor: tooltipBackgroundColor,
				titleColor: tooltipTextColor,
				bodyColor: tooltipTextColor,
				borderColor: '#4B5563',
				borderWidth: 1,
				titleFont: { size: fontSize, family: 'poppins', weight: 'normal' },
				bodyFont: { size: fontSize - 2, family: 'poppins' },
				callbacks: {
					title: tooltipItems => {
						if (tooltipItems && tooltipItems.length > 0) {
							const day = tooltipItems[0].parsed.x
							return t('tooltipTitle', { day })
						}
						return ''
					},
					label: tooltipItem => {
						const points = tooltipItem.parsed.y
						return t('tooltipPoints', { points })
					},
				},
			},
			annotation: {
				annotations: {
					silver: {
						type: 'line',
						yMin: levelThreshold.silver,
						yMax: levelThreshold.silver,
						borderColor: '#a5a5a5',
						borderWidth: 2,
						drawTime: 'beforeDatasetsDraw',
						label: {
							drawTime: 'afterDatasetsDraw',
							padding: { bottom: 16 },
							font: { size: fontSize, family: 'poppins', weight: 'lighter' },
							color: '#a7a7a7',
							backgroundColor: 'transparent',
							content: t('silver'),
							display: true,
							position: 'start',
						},
					},
					gold: {
						type: 'line',
						yMin: levelThreshold.gold,
						yMax: levelThreshold.gold,
						borderColor: '#ffb30d',
						borderWidth: 2,
						drawTime: 'beforeDatasetsDraw',
						label: {
							drawTime: 'afterDatasetsDraw',
							padding: { bottom: 16 },
							font: { size: fontSize, family: 'poppins', weight: 'lighter' },
							color: '#ffb30d',
							backgroundColor: 'transparent',
							content: t('gold'),
							display: true,
							position: 'start',
						},
					},
					platinum: {
						type: 'line',
						yMin: levelThreshold.platinum,
						yMax: levelThreshold.platinum,
						borderColor: '#d9d9d9',
						borderWidth: 2,
						drawTime: 'beforeDatasetsDraw',
						label: {
							drawTime: 'afterDatasetsDraw',
							padding: { bottom: 16 },
							font: { size: fontSize, family: 'poppins', weight: 'lighter' },
							color: '#939393',
							backgroundColor: 'transparent',
							content: t('platinum'),
							display: true,
							position: 'start',
						},
					},
				},
			},
		},
		clip: 10,
		scales: {
			x: {
				min: 1,
				max: daysInObservedMonth,
				display: true,
				grid: { display: false },
				type: 'linear',
				border: { display: false },
				ticks: {
					color: xTickColor,
					font: { size: fontSize },
					callback: function (value) {
						const day = this.getLabelForValue(value as number)
						const allowedDays = [1, 5, 10, 15, 20, 25, 28, 30, 31]
						return allowedDays.includes(Number(day)) ? day : ''
					},
				},
			},
			y: {
				display: true,
				grid: { color: gridColor, tickColor: 'transparent' },
				border: { display: false },
				ticks: {
					color: leftTickColor,
					font: { size: fontSize },
					stepSize: 200,
					callback: function (value) {
						return Number(value) % 200 === 0 ? value : ''
					},
				},
				min: 0,
				max: Math.ceil(Math.max(levelThreshold.platinum + 100, maxValue) / 100) * 100,
			},
		},
		interaction: { intersect: false, mode: 'index' },
		elements: {
			point: {
				hoverBackgroundColor: '#F59E0B',
				hoverBorderColor: '#FFFFFF',
				hoverBorderWidth: 2,
			},
		},
	}
}
